import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, MapPin, AlertCircle, CheckCircle2, Search, CalendarClock } from 'lucide-react';
import type { GapStatsResponse, GapCandidate } from '@wingman/shared';
import { api } from '../../lib/api.js';

interface GapPanelProps {
  onFlyTo?: (lat: number, lng: number) => void;
}

const STATUS_META: Record<string, { label: string; color: string; icon: typeof AlertCircle }> = {
  confirmed_gap: { label: 'CONFIRMED GAP', color: '#f59e0b', icon: AlertCircle },
  osm_found: { label: 'IMPORT MISS', color: '#3b82f6', icon: Search },
  resolved: { label: 'RESOLVED', color: '#22c55e', icon: CheckCircle2 },
  pending: { label: 'PENDING', color: '#8b949e', icon: CalendarClock },
  rejected: { label: 'REJECTED', color: '#6b7280', icon: AlertCircle },
};

export default function GapPanel({ onFlyTo }: GapPanelProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [stats, setStats] = useState<GapStatsResponse | null>(null);
  const [candidates, setCandidates] = useState<GapCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (collapsed || stats) return;
    setLoading(true);
    setError(null);

    // Both gap endpoints can 500 on a cold Worker instance (pg pool warm-up
    // race). Retry each once — a warm instance almost always succeeds.
    const withRetry = async <T,>(fn: () => Promise<T>, attempts = 2): Promise<T> => {
      let lastErr: unknown;
      for (let i = 0; i < attempts; i++) {
        try {
          return await fn();
        } catch (e) {
          lastErr = e;
          await new Promise((r) => setTimeout(r, 400));
        }
      }
      throw lastErr;
    };

    // Stats is the primary data source — the panel renders fully from it.
    withRetry(() => api.gaps.stats())
      .then(setStats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    // List is best-effort enrichment for the "recently resolved" row.
    withRetry(() => api.gaps.list({ limit: 20 }))
      .then((l) => setCandidates(l.candidates))
      .catch(() => {}); // swallow — panel still works from stats alone
  }, [collapsed, stats]);

  const totalGaps = stats?.gaps.byStatus.reduce((a, s) => a + s.count, 0) ?? 0;
  const totalGapEvents = stats?.gaps.byStatus.reduce((a, s) => a + s.totalEvents, 0) ?? 0;
  const confirmedCount = stats?.gaps.byStatus.find((s) => s.status === 'confirmed_gap')?.count ?? 0;
  const resolvedCount = stats?.gaps.byStatus.find((s) => s.status === 'resolved')?.count ?? 0;
  const resolvedCandidates = candidates.filter((c) => c.status === 'resolved');

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 24 }}
      transition={{ duration: 0.22 }}
      className="gap-panel"
    >
      <div className="gap-panel-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="gap-panel-title">
          <MapPin size={12} />
          <span>DATA GAPS</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {totalGaps > 0 && (
            <span className="gap-panel-badge">{totalGaps}</span>
          )}
          <button type="button" className="briefing-card-collapse-btn">
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="gap-panel-body"
          >
            {loading && (
              <div className="gap-panel-loading">Scanning gaps...</div>
            )}
            {error && !stats && (
              <div className="gap-panel-error">{error}</div>
            )}

            {stats && !loading && (
              <>
                {/* Summary stats row */}
                <div className="gap-panel-stats">
                  <div className="gap-stat">
                    <span className="gap-stat-value" style={{ color: '#f59e0b' }}>{confirmedCount}</span>
                    <span className="gap-stat-label">GAPS</span>
                  </div>
                  <div className="gap-stat">
                    <span className="gap-stat-value" style={{ color: '#22c55e' }}>{resolvedCount}</span>
                    <span className="gap-stat-label">RESOLVED</span>
                  </div>
                  <div className="gap-stat">
                    <span className="gap-stat-value" style={{ color: '#00f5ff' }}>{totalGapEvents}</span>
                    <span className="gap-stat-label">EVENTS</span>
                  </div>
                </div>

                {/* Status breakdown */}
                <div className="gap-panel-section-label">STATUS</div>
                <div className="gap-panel-status-list">
                  {stats.gaps.byStatus.map((s) => {
                    const meta = STATUS_META[s.status] ?? STATUS_META.pending;
                    const Icon = meta.icon;
                    return (
                      <div key={s.status} className="gap-status-row">
                        <Icon size={10} style={{ color: meta.color }} />
                        <span className="gap-status-name">{meta.label}</span>
                        <span className="gap-status-count">{s.count}</span>
                        <span className="gap-status-events">{s.totalEvents} ev</span>
                      </div>
                    );
                  })}
                </div>

                {/* Top gaps — rendered from the reliable /stats endpoint */}
                {stats.gaps.topGaps.length > 0 && (
                  <>
                    <div className="gap-panel-section-label">TOP GAPS — NEED PLACES</div>
                    <div className="gap-panel-candidates">
                      {stats.gaps.topGaps.map((g) => (
                        <button
                          key={`${g.name}-${g.lat}-${g.lng}`}
                          type="button"
                          className="gap-candidate-row"
                          onClick={() => g.lat && g.lng && onFlyTo?.(g.lat, g.lng)}
                        >
                          <span className="gap-candidate-name">{g.name}</span>
                          <span className="gap-candidate-events">{g.eventCount} events</span>
                          {g.lat && g.lng && <MapPin size={10} className="gap-candidate-pin" />}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* Recently resolved — best-effort from the list endpoint */}
                {resolvedCandidates.length > 0 && (
                  <>
                    <div className="gap-panel-section-label">RECENTLY RESOLVED</div>
                    <div className="gap-panel-candidates">
                      {resolvedCandidates.slice(0, 5).map((c) => (
                        <div key={c.id} className="gap-candidate-row gap-candidate-resolved">
                          <CheckCircle2 size={10} style={{ color: '#22c55e' }} />
                          <span className="gap-candidate-name">{c.name}</span>
                          <span className="gap-candidate-events">{c.resolutionMethod}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Place health */}
                {stats.places && (
                  <>
                    <div className="gap-panel-section-label">PLACE HEALTH</div>
                    <div className="gap-panel-health">
                      <span>{stats.places.verified} verified</span>
                      <span>{stats.places.stale} stale</span>
                      <span>{stats.places.due_for_validation} due</span>
                    </div>
                  </>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
